const viteServerUrl = (import.meta as any).env?.VITE_SERVER_URL as string | undefined;

function apiBase(): string {
  if (typeof window === 'undefined') return '';
  if (viteServerUrl) {
    if (viteServerUrl.startsWith('http')) return viteServerUrl;
    return `${window.location.protocol}//${viteServerUrl}`;
  }
  return window.location.origin;
}

export const API_BASE = apiBase();
export const TOKEN_KEY = 'bura_crm_token';
export const USER_KEY = 'bura_crm_user';

export interface CrmPrincipal {
  id: string;
  username: string;
  displayName: string;
  role: 'super_admin' | 'admin' | 'dealer_manager' | 'viewer' | 'affiliate';
  permissions: string[];
}

export interface ApiError {
  error: string;
  perm?: string;
  issues?: unknown;
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) { onUnauthorized = fn; }

function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getStoredUser(): CrmPrincipal | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
export function setStoredUser(u: CrmPrincipal | null) {
  if (typeof window === 'undefined') return;
  if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
  else localStorage.removeItem(USER_KEY);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // Content-Type ставим ТОЛЬКО когда есть тело. Иначе fetch'у с пустым
  // body и Content-Type: application/json браузер/Fastify ругаются:
  //   "Body cannot be empty when content-type is set to 'application/json'"
  // Это било POST /api/crm/rooms/:id/force-end и /rake/withdraw, которые
  // не принимают тела (вся инфа в path).
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  if (res.status === 401) {
    if (onUnauthorized) onUnauthorized();
    throw await toError(res);
  }
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function toError(res: Response): Promise<ApiError & Error> {
  let payload: any = { error: `HTTP_${res.status}` };
  try { payload = await res.json(); } catch { /* */ }
  const e: any = new Error(payload?.error || `HTTP_${res.status}`);
  Object.assign(e, payload);
  return e as ApiError & Error;
}

export const api = {
  get: <T,>(path: string) => request<T>('GET', path),
  post: <T,>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T,>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T,>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T,>(path: string) => request<T>('DELETE', path)
};
